import { defaultFaqEntries } from "../data/faq/defaultFaq";
import { partnersFaqEntries } from "../data/faq/partnersFaq";
import { STATE_LANDING_FAQ_BY_PATH } from "../data/faq/stateLandingFaqs";
import { buildFaqPageJsonLd } from "./faqStructuredData";

export const BASE_URL = "https://www.energea.com.br";
export const DEFAULT_OG_IMAGE =
  "https://res.cloudinary.com/energea/image/upload/v1608562947/energea/br-images/EnergeaBrasil_og_image.png";

const withDefaultImage = (meta) =>
  Object.assign({}, meta, {
    ogImage: meta.ogImage || DEFAULT_OG_IMAGE,
    ogImageWidth: meta.ogImageWidth || "1200",
    ogImageHeight: meta.ogImageHeight || "630",
    twitterImage: meta.twitterImage || meta.ogImage || DEFAULT_OG_IMAGE,
  });

export const HOME_PAGE_META = withDefaultImage({
  title: "Energea Brasil | Economia de energia para empresas",
  description:
    "Reduza o custo de energia da sua empresa com a Energea Brasil. Simule economia e cadastre-se em minutos.",
  canonical: `${BASE_URL}/`,
  ogType: "website",
  jsonLd: [buildFaqPageJsonLd(`${BASE_URL}/`, defaultFaqEntries)].filter(
    Boolean
  ),
});

export const REGISTER_PAGE_META = withDefaultImage({
  title: "Cadastro | Energea Brasil",
  description:
    "Cadastre sua empresa na Energea Brasil em poucos passos e comece sua jornada de economia de energia.",
  canonical: `${BASE_URL}/register`,
  ogType: "website",
});

export const PARTNERS_PAGE_META = withDefaultImage({
  title: "Programa de Parceiros | Energea Brasil",
  description:
    "Conheça o programa de parceiros Energea e descubra como gerar receita recorrente com energia limpa.",
  canonical: `${BASE_URL}/parceiros`,
  ogType: "website",
  jsonLd: [
    buildFaqPageJsonLd(`${BASE_URL}/parceiros`, partnersFaqEntries),
  ].filter(Boolean),
});

export const REFERRAL_PAGE_META = withDefaultImage({
  title: "Programa de Indicação | Energea Brasil",
  description:
    "Compartilhe seu link de indicação, acompanhe indicações pendentes e visualize créditos de indicação na Energea Brasil.",
  canonical: `${BASE_URL}/indicacao`,
  ogType: "website",
});

export const SALES_PARTNER_REGISTER_META = withDefaultImage({
  title: "Cadastro de Parceiro | Energea Brasil",
  description:
    "Faça seu cadastro de parceiro e comece a expandir sua atuação com a Energea Brasil.",
  canonical: `${BASE_URL}/cadastro-parceiro`,
  ogType: "website",
});

const PAID_MEDIA_LANDING_PATH = "/energia-por-assinatura";
const PROGRESSIVE_REGISTRATION_LANDING_PATH =
  "/cadastro-energia-por-assinatura";
const BILL_SAVINGS_VIDEO_LANDING_PATH = "/economia-na-conta-de-luz";
const PAID_MEDIA_LANDING_FAQ = [
  {
    question: "Preciso trocar de distribuidora?",
    answer:
      "Não. Você continua recebendo a conta da distribuidora local, com créditos de energia aplicados quando a adesão é aprovada.",
  },
  {
    question: "Tenho que instalar placas solares?",
    answer:
      "Não. A energia vem de projetos de geração compartilhada, sem obra ou instalação no seu endereço.",
  },
  {
    question: "Quem pode contratar?",
    answer:
      "Pessoas físicas e empresas com conta de energia elegível podem se cadastrar para análise.",
  },
  {
    question: "A economia é garantida?",
    answer:
      "A página usa uma estimativa de até 40%. A proposta final depende da análise da conta, disponibilidade e regras aplicáveis.",
  },
];

export const PAID_MEDIA_LANDING_META = withDefaultImage({
  title:
    "Energia por Assinatura | Economize até 40% na conta de luz | Energea Brasil",
  description:
    "Contrate energia por assinatura com a Energea Brasil e reduza sua conta de luz em até 40%, sem instalar placas solares e sem investimento inicial.",
  canonical: `${BASE_URL}${PAID_MEDIA_LANDING_PATH}`,
  ogTitle: "Energia por assinatura Energea Brasil com economia de até 40%",
  ogDescription:
    "Simule sua economia em energia limpa por assinatura para PF ou PJ.",
  ogUrl: `${BASE_URL}${PAID_MEDIA_LANDING_PATH}`,
  ogType: "website",
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Energia por assinatura Energea Brasil",
      description:
        "Landing page da Energea Brasil para simular economia de até 40% com energia por assinatura.",
      url: `${BASE_URL}${PAID_MEDIA_LANDING_PATH}`,
      inLanguage: "pt-BR",
      isPartOf: {
        "@type": "WebSite",
        name: "Energea Brasil",
        url: BASE_URL,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Energea Brasil",
          item: BASE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Energia por assinatura",
          item: `${BASE_URL}${PAID_MEDIA_LANDING_PATH}`,
        },
      ],
    },
    buildFaqPageJsonLd(
      `${BASE_URL}${PAID_MEDIA_LANDING_PATH}`,
      PAID_MEDIA_LANDING_FAQ
    ),
  ].filter(Boolean),
});

export const PROGRESSIVE_REGISTRATION_LANDING_META = withDefaultImage({
  title: "Cadastro de Energia por Assinatura | Energea Brasil",
  description:
    "Cadastre-se em etapas para simular sua economia com energia por assinatura da Energea Brasil. Para pessoas físicas e empresas, sem instalar placas solares.",
  canonical: `${BASE_URL}${PROGRESSIVE_REGISTRATION_LANDING_PATH}`,
  ogTitle: "Cadastre-se para economizar com energia por assinatura",
  ogDescription:
    "Faça seu cadastro em etapas e descubra sua economia estimada na conta de luz.",
  ogUrl: `${BASE_URL}${PROGRESSIVE_REGISTRATION_LANDING_PATH}`,
  ogType: "website",
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Cadastro de energia por assinatura Energea Brasil",
      description:
        "Cadastro em etapas para simular economia na conta de luz com energia por assinatura.",
      url: `${BASE_URL}${PROGRESSIVE_REGISTRATION_LANDING_PATH}`,
      inLanguage: "pt-BR",
      isPartOf: {
        "@type": "WebSite",
        name: "Energea Brasil",
        url: BASE_URL,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Energea Brasil",
          item: BASE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Cadastro de energia por assinatura",
          item: `${BASE_URL}${PROGRESSIVE_REGISTRATION_LANDING_PATH}`,
        },
      ],
    },
    buildFaqPageJsonLd(
      `${BASE_URL}${PROGRESSIVE_REGISTRATION_LANDING_PATH}`,
      PAID_MEDIA_LANDING_FAQ
    ),
  ].filter(Boolean),
});

export const BILL_SAVINGS_VIDEO_LANDING_META = withDefaultImage({
  title: "Economia na Conta de Luz | Economize até 40% | Energea Brasil",
  description:
    "Veja como reduzir a conta de luz em até 40% com a Energea Brasil, sem placas solares, sem obra e com contratação digital de energia por assinatura.",
  canonical: `${BASE_URL}${BILL_SAVINGS_VIDEO_LANDING_PATH}`,
  ogTitle: "Economia na conta de luz com a Energea Brasil",
  ogDescription: "Simule sua economia em energia por assinatura para PF ou PJ.",
  ogUrl: `${BASE_URL}${BILL_SAVINGS_VIDEO_LANDING_PATH}`,
  ogType: "website",
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Economia na conta de luz Energea Brasil",
      description:
        "Landing page da Energea Brasil para simular economia de até 40% na conta de luz com energia por assinatura.",
      url: `${BASE_URL}${BILL_SAVINGS_VIDEO_LANDING_PATH}`,
      inLanguage: "pt-BR",
      isPartOf: {
        "@type": "WebSite",
        name: "Energea Brasil",
        url: BASE_URL,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Energea Brasil",
          item: BASE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Economia na conta de luz",
          item: `${BASE_URL}${BILL_SAVINGS_VIDEO_LANDING_PATH}`,
        },
      ],
    },
    buildFaqPageJsonLd(
      `${BASE_URL}${BILL_SAVINGS_VIDEO_LANDING_PATH}`,
      PAID_MEDIA_LANDING_FAQ
    ),
  ].filter(Boolean),
});

export const STATE_LANDING_PAGES = [
  {
    path: "/energia-mais-barata-minas-gerais",
    stateName: "Minas Gerais",
    stateCode: "MG",
    landingLabel: "Energia mais barata em Minas Gerais",
    title:
      "Energia mais barata em Minas Gerais | Economizar conta de luz MG | Energea Brasil",
    description:
      "Energia mais barata em Minas Gerais para empresas e condomínios. Economize a conta de luz MG com a Energea, mercado livre de energia em Minas Gerais e geração distribuída com adesão 100% digital.",
  },
  {
    path: "/energia-mais-barata-rio-de-janeiro",
    stateName: "Rio de Janeiro",
    stateCode: "RJ",
    landingLabel: "Energia mais barata no Rio de Janeiro",
    title:
      "Energia mais barata no Rio de Janeiro | Economizar conta de luz RJ | Energea Brasil",
    description:
      "Energia mais barata no Rio de Janeiro para empresas e condomínios. Economize a conta de luz RJ com a Energea, mercado livre de energia no Rio de Janeiro e adesão 100% digital.",
  },
  {
    path: "/energia-mais-barata-goias",
    stateName: "Goiás",
    stateCode: "GO",
    landingLabel: "Energia mais barata em Goiás",
    title:
      "Energia mais barata em Goiás | Economizar conta de luz GO | Energea Brasil",
    description:
      "Energia mais barata em Goiás para empresas e condomínios. Economize a conta de luz GO com a Energea, mercado livre de energia em Goiás e geração distribuída com adesão 100% digital.",
  },
].map((page) => {
  const faqContent = STATE_LANDING_FAQ_BY_PATH[page.path];

  return Object.assign({}, page, {
    faqCategories: (faqContent && faqContent.categories) || [],
    faqEntries: (faqContent && faqContent.entries) || [],
  });
});

export const getStateLandingSeo = ({
  path,
  landingLabel,
  title,
  description,
  faqEntries,
}) => {
  const canonical = `${BASE_URL}${path}`;

  return withDefaultImage({
    title,
    description,
    canonical,
    ogTitle: title,
    ogDescription: description,
    ogUrl: canonical,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        description,
        url: canonical,
        inLanguage: "pt-BR",
        isPartOf: {
          "@type": "WebSite",
          name: "Energea Brasil",
          url: BASE_URL,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Energea Brasil",
            item: BASE_URL,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: landingLabel,
            item: canonical,
          },
        ],
      },
      buildFaqPageJsonLd(canonical, faqEntries),
    ].filter(Boolean),
  });
};

export const getGlossaryIndexSeo = (terms) =>
  withDefaultImage({
    title: "Glossário de Energia para Empresas | Energea Brasil",
    description:
      "Glossário em PT-BR com termos de energia, tarifa e contratos para empresas. Entenda rápido e cadastre-se para começar a economizar.",
    canonical: `${BASE_URL}/glossario`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        name: "Glossário de Energia para Empresas - Energea Brasil",
        inLanguage: "pt-BR",
        description:
          "Termos essenciais para empresas que querem reduzir custos de energia com segurança.",
        url: `${BASE_URL}/glossario`,
        hasDefinedTerm: terms.map((term) => ({
          "@type": "DefinedTerm",
          name: term.term,
          description: term.description,
          url: `${BASE_URL}/glossario/${term.slug}`,
          inDefinedTermSet: `${BASE_URL}/glossario`,
        })),
      },
    ],
  });

export const getGlossaryTermSeo = (term) =>
  withDefaultImage({
    title: `${term.term} | Glossário Energea Brasil`,
    description: term.description,
    canonical: `${BASE_URL}/glossario/${term.slug}`,
    ogType: "article",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "DefinedTerm",
        name: term.term,
        description: term.description,
        termCode: term.slug,
        inDefinedTermSet: `${BASE_URL}/glossario`,
        url: `${BASE_URL}/glossario/${term.slug}`,
        inLanguage: "pt-BR",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Glossário",
            item: `${BASE_URL}/glossario`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: term.term,
            item: `${BASE_URL}/glossario/${term.slug}`,
          },
        ],
      },
    ],
  });
